import type { ReactNode } from "react";

/**
 * Subtle 5-step engagement guide. Renders one horizontal row of numbered
 * pills with three possible states per step:
 *
 *   done    — quiet, black numeral chip, no action
 *   next    — crimson numeral chip + inline primary button with the
 *             one-shot action the operator should take next
 *   blocked — the step can't progress (e.g. service agreement unsigned)
 *
 * Intentionally restrained — no big tooltips, no walkthrough overlay,
 * no "tap here" hand-holding. Operators see at a glance where the
 * client is in the pipeline and the single next click that moves it.
 */
export type StepState = "done" | "next" | "blocked" | "todo";

export interface Step {
  label: string;
  state: StepState;
  /** Only rendered when state === "next". Shown as a small inline button. */
  actionLabel?: string;
  onAction?: () => void;
}

/**
 * Screen-reader prefix per state. State is NOT conveyed by color alone —
 * SR users hear "Done:", "Up next:", "Blocked:", or "To do:" before the
 * step label, so meaning survives without the crimson/black distinction.
 */
const STATE_PREFIX: Record<StepState, string> = {
  done: "Done",
  next: "Up next",
  blocked: "Blocked",
  todo: "To do",
};

export function NextStepCard({
  steps,
  label = "Next step",
}: {
  steps: Step[];
  label?: string;
}): ReactNode {
  // The inline action sits to the right of the row, sourced from the
  // single "next" step. If there isn't one, the action slot stays empty.
  const next = steps.find((s) => s.state === "next");

  // Descriptive top-level label: "Next step: <the actual next step label>"
  // falls back to the generic label when nothing is queued.
  const ariaLabel = next ? `${label}: ${next.label}` : label;

  return (
    <div className="next-step-card" role="status" aria-label={ariaLabel}>
      <span className="next-step-label" aria-hidden="true">
        {label}
      </span>
      <ol className="next-step-list">
        {steps.map((s, i) => (
          <li key={i} className={`next-step-item ${s.state}`}>
            {/* Numeral is decorative — the label already says what step it is. */}
            <span className="num" aria-hidden="true">
              {i + 1}
            </span>
            {/* SR-only state prefix so meaning isn't color-coded. */}
            <span className="sr-only">{`${STATE_PREFIX[s.state]}: `}</span>
            <span>{s.label}</span>
          </li>
        ))}
      </ol>
      {next?.actionLabel && next.onAction && (
        <div className="next-step-action">
          <button
            type="button"
            className="btn-primary"
            onClick={next.onAction}
            aria-label={`${next.actionLabel} (${next.label})`}
          >
            {next.actionLabel}
          </button>
        </div>
      )}
    </div>
  );
}
